/**
 * Finish a social sign-up — `SIGN_UP_CONTINUE_PATH` (@tonnie/sdk).
 *
 * Clerk sends a sign-up here when the provider left a gap. The one this
 * instance can leave is an email Clerk must see proven (Microsoft does not
 * vouch for its addresses): a 6-digit code goes to it, the person types it,
 * and the account is made. Anything else Clerk asks for is not something this
 * product collects — it is said, reported, and the way back offered.
 *
 * Renders `useSignUpContinuation` in the sign-up frame; a finished sign-up
 * goes where Clerk bound it, else to the portal's `afterSignUp`.
 */
import { AlertTriangle, Loader2 } from '@tonnie/assets';
import { useAuthErrorMessage, useTranslation } from '@tonnie/locale';
import { useErrorCapture } from '@tonnie/observability';
import { useSignUpContinuation } from '@tonnie/sdk';
import { Button } from '@tonnie/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'wouter';

import { goToDestination, useAccountPortal } from '../portal';
import { AccountAuthLayout } from './AccountAuthLayout';
import { CodeStep } from './CodeStep';

export function SignUpContinueView() {
  const { t } = useTranslation();
  const { afterSignUp } = useAccountPortal();
  const describeAuthError = useAuthErrorMessage();
  const { captureException } = useErrorCapture();
  const [, setLocation] = useLocation();
  const { state, completeUrl, codeSendError, sendCode, verify } = useSignUpContinuation();
  const [error, setError] = useState<string | null>(null);
  const reportedUnsupported = useRef(false);

  const say = useCallback(
    (err: unknown, fallback: string) => {
      const failure = describeAuthError(err, fallback);
      if (!failure.expected) {
        captureException(err instanceof Error ? err : new Error(String(err)), {
          component: 'SignUpContinueView',
        });
      }
      setError(failure.message);
    },
    [describeAuthError, captureException]
  );

  const goOn = useCallback(
    () => goToDestination(completeUrl ?? afterSignUp(), setLocation),
    [completeUrl, afterSignUp, setLocation]
  );

  // Nothing in progress — the page was opened directly; a finished one moves on.
  useEffect(() => {
    if (state.step === 'none') setLocation('/signup');
    if (state.step === 'complete') goOn();
  }, [state.step, setLocation, goOn]);

  useEffect(() => {
    if (codeSendError) say(codeSendError, t('auth.errors.unableToResend'));
  }, [codeSendError, say, t]);

  useEffect(() => {
    if (state.step !== 'unsupported' || reportedUnsupported.current) return;
    reportedUnsupported.current = true;
    captureException(new Error(`Social sign-up asks for: ${state.missing.join(', ')}`), {
      component: 'SignUpContinueView',
    });
  }, [state, captureException]);

  if (state.step === 'verify_email') {
    return (
      <AccountAuthLayout
        brand="sign-up"
        title={t('auth.flow.signUp.codeTitle')}
        description={t('auth.flow.code.description', { email: state.email })}
        onBack={() => setLocation('/signup')}
      >
        <CodeStep
          label={t('auth.flow.code.label')}
          submitLabel={t('auth.flow.signUp.codeSubmit')}
          error={error}
          testIdPrefix="auth-signup-continue"
          onSubmit={async (code) => {
            setError(null);
            const result = await verify(code);
            if (result.outcome === 'done') goOn();
            else say(result.error, t('auth.errors.invalidVerificationCode'));
          }}
          onResend={async () => {
            setError(null);
            return (await sendCode()).outcome === 'done';
          }}
        />
      </AccountAuthLayout>
    );
  }

  if (state.step === 'unsupported') {
    return (
      <AccountAuthLayout
        brand="sign-up"
        eyebrow={
          <AlertTriangle
            className="mb-2 h-10 w-10 text-destructive-accessible"
            aria-hidden="true"
          />
        }
        title={t('auth.signUpContinue.unsupported')}
        description={t('auth.signUpContinue.unsupportedDesc')}
      >
        <Button asChild className="h-11 text-base font-semibold">
          <Link href="/signup" data-testid="auth-signup-continue-unsupported">
            {t('auth.signUpContinue.startAgain')}
          </Link>
        </Button>
      </AccountAuthLayout>
    );
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-card" role="status">
      <Loader2
        className="h-8 w-8 animate-spin text-primary-accessible motion-reduce:animate-none"
        aria-label={t('auth.social.signingIn')}
      />
    </div>
  );
}
